// User Guide — short, scannable help for the web app. Lives entirely on the
// client (no API calls) so it stays usable even when the backend is down.

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  MdMenuBook, MdSearch, MdDashboard, MdAddBox, MdList, MdLocationOn,
  MdSwapHoriz, MdAssignment, MdDescription, MdStorage, MdRouter,
  MdBarChart, MdInsights, MdHistory, MdPeople, MdSettings, MdHelpOutline,
  MdPictureAsPdf, MdDownload, MdViewList,
} from 'react-icons/md';

const SECTIONS = [
  {
    id: 'getting-started',
    title: 'Getting started',
    icon: <MdHelpOutline />,
    color: '#2196F3',
    body: [
      ['Sign in', 'Use the credentials your administrator provided. The first-run admin is `admin` / `tagmyassets123` — change it immediately under Settings → Users.'],
      ['Sidebar', 'The left sidebar shows every module you have access to. Admins see all modules; operators only see what permissions they were granted.'],
      ['Mobile', 'The web app is mobile-friendly — every page reflows for phones. Field staff typically use the dedicated Android app for hardware-scanner workflows.'],
    ],
  },
  {
    id: 'dashboard',
    title: 'Dashboard',
    icon: <MdDashboard />,
    color: '#2196F3',
    body: [
      ['What it shows', 'Real-time "now" view: total assets, active count, open audits, last-touched asset, online users, status pie, and per-location bar chart.'],
      ['Refresh', 'The dashboard auto-refreshes via WebSocket whenever any user creates / edits / scans something — no manual reload needed.'],
    ],
  },
  {
    id: 'assets',
    title: 'Asset Registration & All Assets',
    icon: <MdAddBox />,
    color: '#4CAF50',
    body: [
      ['Pick a template', 'Asset Registration → choose a Template (Classic, Plant & Machinery, Furniture & Fixtures, Computer, or one you built). The form fields change to match the template.'],
      ['Register an asset', 'Fill the fields the template shows. Dropdowns come from your Masters; the Asset General Name auto-fills the Category; GPS + timestamp are captured automatically; the Asset ID is generated with the template prefix (e.g. FF-000123). RFID is optional unless the template requires it.'],
      ['Bulk import', 'The "Template" button downloads an Excel matching the selected template\'s columns (with dropdowns). Fill it in and use "Import" — values are validated against your masters and Asset IDs are generated automatically.'],
      ['Edit / move / retire', 'All Assets → click any row to open the detail page. Status changes (active → maintenance → retired → disposed → lost) and location moves are tracked in the asset\'s history.'],
      ['Print labels', 'Select rows in All Assets, click "Generate Labels" — produces a printable sheet with QR/barcode plus asset name.'],
    ],
  },
  {
    id: 'masters',
    title: 'Masters & Templates',
    icon: <MdViewList />,
    color: '#5B2150',
    body: [
      ['Master lists', 'Masters → Master Lists holds the lookup libraries that feed registration dropdowns (Asset Description, Department, Division, Floor, Brand, Working Condition, Surface for RFID, etc.). Lists marked "user-add" let operators type a new value during registration and it is saved for next time.'],
      ['Asset General Names', 'A single shared library (managed centrally by the platform owner) mapping each general name to its category. Choosing a general name during registration auto-fills the Asset Category.'],
      ['Registration Templates', 'Masters → Registration Templates lists your templates. Click "View / Edit" on any template (including the built-ins) to open the field builder — add / reorder / remove fields and set each field\'s label, type, backing master, whether it\'s required, and the Asset-ID prefix. Built-in templates keep their name locked and can\'t be deleted; use "Clone" to make a fully independent copy or a brand-new one via "New template".'],
      ['Access', 'The Masters module is admin-only. The shared Asset General Name library is editable only by the platform owner.'],
    ],
  },
  {
    id: 'locations',
    title: 'Location Master',
    icon: <MdLocationOn />,
    color: '#00BCD4',
    body: [
      ['Hierarchy', 'Locations support parent/child nesting (e.g. Building → Floor → Room). Audits can include sub-locations via the "Include sub-locations" toggle.'],
      ['Location codes', 'Each location has a short code. Use codes you can write on physical signage or in spreadsheets for quick reference.'],
    ],
  },
  {
    id: 'movements',
    title: 'Asset Movement',
    icon: <MdSwapHoriz />,
    color: '#9C27B0',
    body: [
      ['Move single asset', 'Pick the asset, choose destination location, optionally enter a reason. The previous location is recorded automatically.'],
      ['Bulk move', 'Select multiple assets in All Assets, click "Move" — set destination once, applied to all.'],
      ['Movement history', 'Every move creates a row in the asset\'s history; visible in the asset detail page and in Reports.'],
    ],
  },
  {
    id: 'audit',
    title: 'Asset Audit',
    icon: <MdAssignment />,
    color: '#FF9800',
    body: [
      ['Start an audit', 'Pick one or more locations from the dropdown. Optionally name the audit. "Include sub-locations" pulls in nested locations\' assets.'],
      ['Scanning', 'Use a fixed RFID reader, the Android handheld, or the web simulator. Each scan is matched against the audit\'s expected assets in real time.'],
      ['Pause / resume', 'You can pause an audit and resume later. Multi-location audits keep state across sessions.'],
      ['Complete', 'Click "Complete" when done — the audit is sealed (no more edits) and an emailed report is generated to subscribers.'],
    ],
  },
  {
    id: 'audit-report',
    title: 'Audit Report',
    icon: <MdDescription />,
    color: '#795548',
    body: [
      ['View past audits', 'Filter by status (in progress / completed / paused) or jump straight in via deep-link from the Dashboard.'],
      ['Compare audits', 'Pick a baseline and a target audit; the comparison view shows what was found / lost / appeared between the two dates.'],
      ['Export', 'Excel and PDF exports include scanned, missing, unexpected, and notes for each item.'],
    ],
  },
  {
    id: 'raw-inventory',
    title: 'Raw Inventory',
    icon: <MdStorage />,
    color: '#607D8B',
    body: [
      ['What it captures', 'Raw RFID reads that aren\'t yet linked to a registered asset — e.g. brand-new tags or signal noise. Useful for investigating unexpected reads.'],
      ['Promote', 'Click any raw scan → "Register as asset" to convert it into a proper asset record without re-typing the tag.'],
    ],
  },
  {
    id: 'reader-manager',
    title: 'Reader Manager',
    icon: <MdRouter />,
    color: '#3F51B5',
    body: [
      ['Add a reader', 'Provide IP address, port, model, and antenna count. The system polls health every 30 seconds and will alert if a reader goes offline.'],
      ['RF settings', 'Tune antenna power and dwell time per reader. Conservative defaults work for most warehouse layouts.'],
      ['Push receivers', 'External readers (Impinj / Zebra / generic) can push reads via HTTP — see the per-reader config screen for the URL & token.'],
    ],
  },
  {
    id: 'reports',
    title: 'Reports',
    icon: <MdBarChart />,
    color: '#1565C0',
    body: [
      ['Pre-built reports', 'Asset register, movement history, audit summary, status distribution, location utilisation. Each supports CSV & PDF export.'],
      ['Scheduled reports', 'Settings → Report Schedules → automate any report to email-out on a cron (daily / weekly / monthly).'],
    ],
  },
  {
    id: 'analytics',
    title: 'Analytics',
    icon: <MdInsights />,
    color: '#0097A7',
    body: [
      ['Difference from Dashboard', 'Dashboard shows "right now". Analytics shows ratios, trends and per-asset behaviour over a chosen window (24 h to 1 year).'],
      ['Activity heat-map', 'When in the day / week is your team busiest? Colour intensity shows event count per hour × weekday.'],
      ['Stale assets', 'Assets that haven\'t been moved or scanned in 60+ days — likely candidates for a physical verification.'],
      ['Asset journey', 'Search for any asset → see its complete chronological history (registration, every move, status change, audit appearance).'],
      ['Location coverage', 'Which locations have been audited recently and how accurate those audits were. Red = audited > 90 days ago or accuracy < 60%.'],
    ],
  },
  {
    id: 'activity-logs',
    title: 'Activity Logs',
    icon: <MdHistory />,
    color: '#E91E63',
    body: [
      ['Two tabs', 'User Activity = CRUD on assets / locations / audits / etc. System = login attempts, password changes, DB backups, settings changes.'],
      ['Filters', 'Narrow by user, action keyword, target type, or date range. Click any row to expand the JSON details payload.'],
      ['Retention', 'Settings → Database → Clear Activity Logs lets admins prune old entries; supports "older than N days" and category-scoped clears.'],
    ],
  },
  {
    id: 'users',
    title: 'User Management',
    icon: <MdPeople />,
    color: '#673AB7',
    body: [
      ['Roles', 'Two roles: Admin (full access) and Operator (gated by per-module permissions).'],
      ['Permissions', 'Tick each module the operator should access. Some modules (User Management, Settings, Analytics, Activity Logs) are admin-only and ignore the checklist.'],
      ['Reset password', 'Admins can reset any user\'s password from the user detail row; the user is required to set a new one on next login.'],
    ],
  },
  {
    id: 'settings',
    title: 'Settings',
    icon: <MdSettings />,
    color: '#37474F',
    body: [
      ['Backups & restore', 'Download the full SQLite database as a single .db file. Restore replaces the live DB and pm2 will auto-restart the backend.'],
      ['Categories & reasons', 'Manage the dropdown lists for asset categories and movement reasons.'],
      ['Email & schedules', 'SMTP credentials and per-job schedule cron expressions live here.'],
    ],
  },
  {
    id: 'tips',
    title: 'Tips',
    icon: <MdHelpOutline />,
    color: '#009688',
    body: [
      ['Time zone', 'Server stores timestamps in UTC; the web app displays them in your browser\'s local time zone (typically IST in India).'],
      ['Keyboard shortcuts', 'In tables, click column headers to sort. Many filters accept Enter to apply.'],
      ['Hardware scanners', 'Most USB/Bluetooth scanners emulate keyboard input — focus the scan field and trigger the device. The Android app handles RFID readers natively.'],
    ],
  },
];

const UserGuide = () => {
  const [query, setQuery] = useState('');
  const [active, setActive] = useState('getting-started');

  const filtered = SECTIONS.filter(s => {
    if (!query) return true;
    const q = query.toLowerCase();
    return s.title.toLowerCase().includes(q)
        || s.body.some(([t, b]) => (t + b).toLowerCase().includes(q));
  });

  const activeSection = filtered.find(s => s.id === active) || filtered[0];

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <div className="page-title"><MdMenuBook /> User Guide</div>
          <div className="page-subtitle">Short, scannable how-tos for every module — searchable</div>
        </div>
      </div>

      {/* Full manual (PDF) */}
      <div className="card" style={{
        marginBottom: 16, display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap',
        background: 'linear-gradient(135deg, var(--primary), var(--accent))', color: '#fff', border: 'none',
      }}>
        <MdPictureAsPdf style={{ fontSize: 40, flexShrink: 0 }} />
        <div style={{ flex: 1, minWidth: 220 }}>
          <div style={{ fontWeight: 700, fontSize: 16 }}>Full Operations Manual</div>
          <div style={{ fontSize: 13, opacity: 0.9 }}>
            The complete Tag My Assets guide — web platform &amp; Android field app — with screenshots. Get the PDF from Downloads.
          </div>
        </div>
        <Link to="/downloads" className="btn" style={{ background: '#fff', color: 'var(--primary)', fontWeight: 700, whiteSpace: 'nowrap' }}>
          <MdDownload style={{ verticalAlign: -3, marginRight: 6 }} />Get the manual
        </Link>
      </div>

      {/* Search */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="search-bar" style={{ width: '100%' }}>
          <MdSearch />
          <input placeholder="Search the guide…"
            value={query} onChange={e => setQuery(e.target.value)} />
        </div>
      </div>

      <div className="grid-2col" style={{ display: 'grid', gridTemplateColumns: '260px 1fr', gap: 16 }}>
        {/* Sidebar of sections */}
        <div className="card" style={{ padding: 8, alignSelf: 'flex-start', position: 'sticky', top: 12 }}>
          {filtered.length === 0 ? (
            <div style={{ padding: 16, color: 'var(--text-muted)', fontSize: 13 }}>
              Nothing matches "{query}".
            </div>
          ) : filtered.map(s => (
            <button key={s.id} onClick={() => setActive(s.id)}
              style={{
                display: 'flex', alignItems: 'center', gap: 10, width: '100%',
                padding: '10px 12px', background: active === s.id ? '#e8edf5' : 'transparent',
                border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13,
                color: active === s.id ? 'var(--primary)' : 'var(--text-primary)',
                fontWeight: active === s.id ? 600 : 400,
                textAlign: 'left',
              }}>
              <span style={{
                color: s.color, display: 'inline-flex',
                width: 24, height: 24, alignItems: 'center', justifyContent: 'center',
                fontSize: 18,
              }}>{s.icon}</span>
              {s.title}
            </button>
          ))}
        </div>

        {/* Active section content */}
        <div className="card" style={{ minHeight: 320 }}>
          {activeSection ? (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
                <span style={{
                  width: 44, height: 44, borderRadius: 10,
                  background: activeSection.color + '22', color: activeSection.color,
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 24,
                }}>{activeSection.icon}</span>
                <h2 style={{ margin: 0 }}>{activeSection.title}</h2>
              </div>
              {activeSection.body.map(([title, body], i) => (
                <div key={i} style={{ marginBottom: 16 }}>
                  <div style={{
                    fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 4,
                    borderLeft: `3px solid ${activeSection.color}`, paddingLeft: 10,
                  }}>{title}</div>
                  <div style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6, paddingLeft: 13 }}>
                    {body}
                  </div>
                </div>
              ))}
            </>
          ) : (
            <div className="empty-state">No section selected</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserGuide;
